import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { todayISO, formatDateTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function ReportMandorCheck() {
  const [tanggal, setTanggal] = useState(todayISO());
  const [groups, setGroups] = useState([]);
  const load = (silent) => api.get(`/attendance/mandor-check?tanggal=${tanggal}`).then((r) => setGroups(r.data)).catch((e) => { if (!silent) toast.error(apiErr(e), { id: "mc-err" }); });
  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [tanggal]);
  const tot = (k) => groups.reduce((s, g) => s + g[k].length, 0);

  return (
    <div className="space-y-5">
      <PageHeader title="Cek Absen Mandor" subtitle="Ringkasan harian per mandor: sudah diabsen, belum diabsen, dan ditolak — diperbarui tiap 1 menit" />
      <Card className="p-4 flex flex-wrap items-end gap-4">
        <div className="w-48"><F label="Tanggal"><Input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} data-testid="mc-date" /></F></div>
        <Pill icon={CheckCircle2} tone="emerald" label="Sudah" v={tot("sudah")} testid="mc-total-sudah" />
        <Pill icon={Clock} tone="amber" label="Belum" v={tot("belum")} testid="mc-total-belum" />
        <Pill icon={XCircle} tone="rose" label="Ditolak" v={tot("ditolak")} testid="mc-total-ditolak" />
      </Card>
      {groups.length === 0 && <Card className="p-8 text-center text-slate-400">Tidak ada data tim.</Card>}
      <div className="grid gap-4 lg:grid-cols-2 items-start">
        {groups.map((g, gi) => <MandorCard key={g.mandor_id || "none"} g={g} gi={gi} />)}
      </div>
    </div>
  );
}

const TONES = { emerald: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", rose: "bg-rose-50 text-rose-700" };
const Pill = ({ icon: I, tone, label, v, testid }) => (
  <div className={`flex items-center gap-2 rounded-xl px-4 py-2 ${TONES[tone]}`} data-testid={testid}><I className="h-4 w-4" /><span className="text-sm">{label}</span><b className="font-mono text-lg">{v}</b></div>
);

const MandorCard = ({ g, gi }) => (
  <Card className="overflow-hidden" data-testid={`mc-group-${gi}`}>
    <div className="flex items-center justify-between bg-blue-900 px-4 py-2.5 text-white">
      <b className="font-heading">{g.mandor_nama}</b>
      <span className="text-xs">{g.sudah.length}/{g.total} sudah diabsen</span>
    </div>
    <div className="h-1.5 bg-slate-100"><div className="h-full bg-orange-500" style={{ width: `${g.total ? (g.sudah.length / g.total) * 100 : 0}%` }} /></div>
    <div className="space-y-3 p-4 text-sm">
      {g.ditolak.length > 0 && (
        <Section title={`Ditolak (${g.ditolak.length})`} cls="text-rose-700" testid={`mc-ditolak-${gi}`}>
          {g.ditolak.map((x) => <li key={x.employee_id}><b>{x.nama}</b> — {x.message} <span className="text-xs text-slate-400">(oleh {x.input_by}, {formatDateTime(x.waktu)}{x.jumlah > 1 ? `, ${x.jumlah}x` : ""})</span></li>)}
        </Section>
      )}
      <Section title={`Belum diabsen (${g.belum.length})`} cls="text-amber-700" testid={`mc-belum-${gi}`}>
        {g.belum.length === 0 ? <li className="list-none text-emerald-700">Semua anggota sudah diabsen</li>
          : g.belum.map((x) => <li key={x.employee_id}>{x.nama}{x.proyek_aktif && x.proyek_aktif !== "-" && <span className="text-xs text-slate-400"> · {x.proyek_aktif}</span>}</li>)}
      </Section>
      <details>
        <summary className="cursor-pointer font-semibold text-emerald-700" data-testid={`mc-sudah-${gi}`}>Sudah diabsen ({g.sudah.length})</summary>
        <ul className="mt-1 list-disc pl-5 text-slate-600">
          {g.sudah.map((x) => <li key={x.employee_id}>{x.nama} · {x.status} · {x.proyek} <span className="text-xs text-slate-400">(input: {x.input_by})</span></li>)}
        </ul>
      </details>
    </div>
  </Card>
);

const Section = ({ title, cls, testid, children }) => (
  <div data-testid={testid}><div className={`font-semibold ${cls}`}>{title}</div><ul className="mt-1 list-disc pl-5 text-slate-700">{children}</ul></div>
);
